/**
 * "How this integration works" — prototype/integrations-frames.js's
 * `explainer()`. Same card component everywhere it's used; always expanded,
 * no collapse affordance. Intro/bullet copy ported verbatim from
 * GoogleIntegrationForm.js / OutlookIntegrationForm.js's WelcomeCard usage.
 * Illustration assets already exist (img/integrations_howto/) — the
 * prototype's dashed-box placeholder is explicitly a stand-in for them, not
 * something to copy.
 */
import React from "react";
import Card from "../../theme/untitled/Card/Card";
import appConfig from "../../../../appConfig";
import ServiceAccountEmailField from "./ServiceAccountEmailField";
import styles from "./ExplainerCard.module.scss";

const EXPLAINER_CONTENT = {
  google: {
    providerLabel: "Google",
    intro: `To synchronize with Google Calendar, sign in to Google with your account and grant ${appConfig.displayName} access to the following services:`,
    bullets: [
      {
        label: "Calendar API",
        text: "it will allow us to pull events from your calendars.",
      },
      {
        label: "Events Attachments (optional)",
        text: (
          <>
            if you attach an image to an event, we will pull and use it as an
            event cover image. To grant access to your Events attachments, share
            them with the email address: <ServiceAccountEmailField />
          </>
        ),
      },
      {
        label: "Places (optional)",
        text: "If you set an event location pointing to a place, we will pull and use it for filtering and displaying place details.",
      },
      {
        label: "Public profile",
        text: "we will pull public profile information to display your name on the integration card.",
      },
    ],
    image: "/img/integrations_howto/howto_gcal.png",
  },
  outlook: {
    providerLabel: "Outlook",
    intro: `To synchronize with Outlook Calendar, sign in to Microsoft with your account and grant ${appConfig.displayName} access to the following services:`,
    bullets: [
      {
        label: "Public profile",
        text: "we will pull public profile information to display your name on the integration card.",
      },
      {
        label: "Calendar",
        text: "it will allow us to pull events from your calendars.",
      },
      {
        label: "Events Attachments (optional)",
        text: "if you attach an image to an event, we'll pull it and use it as the event's cover image. No extra sharing step needed — this uses the same calendar access you've already granted.",
      },
    ],
    image: "/img/integrations_howto/howto_outlook.png",
  },
};

/**
 * @param {"google"|"outlook"} provider
 */
const ExplainerCard = ({ provider }) => {
  const content = EXPLAINER_CONTENT[provider];
  const bulletItems = content.bullets.map((bullet) => (
    <li key={bullet.label}>
      <strong>{bullet.label}</strong> — {bullet.text}
    </li>
  ));

  return (
    <Card className={styles.card}>
      <div className={styles.head}>
        <span className={styles.headTitle}>Connect integration</span>
      </div>
      <div className={styles.body}>
        <div className={styles.text}>
          <p className={styles.intro}>{content.intro}</p>
          <ul className={styles.bullets}>{bulletItems}</ul>
        </div>
        <img
          className={styles.illustration}
          src={content.image}
          alt=""
          width={280}
          height={180}
        />
      </div>
    </Card>
  );
};

export default ExplainerCard;
